// =====================================================================
// Aangan — Browser-side image compressor
//
// Resizes and converts user-uploaded images to WebP before they ever
// reach Firebase Storage. Runs entirely in the browser using <canvas>.
//
// Why this matters:
//   - 4 MB iPhone photo → ~150 KB WebP after compression
//   - 95% reduction in upload, storage, and egress costs
//   - Faster uploads on slow connections
//   - Lower data usage for users on mobile
//
// Output guarantees:
//   - Always WebP format
//   - Max dimension 720 px (longer side)
//   - Quality 80% (visually indistinguishable from original)
//   - Aspect ratio preserved
//
// Note: this DOES strip EXIF metadata as a side effect of canvas re-encoding.
// That's actually a good thing for privacy (removes GPS, camera info).
// =====================================================================

const MAX_DIMENSION = 720;          // longer side will be 720px
const QUALITY = 0.80;                // 80% — good balance of size vs quality
const OUTPUT_TYPE = 'image/webp';   // WebP universal browser support

/**
 * Compress an image file to a smaller WebP blob.
 *
 * @param {File} file - User-selected image file (from <input type="file">)
 * @returns {Promise<Blob>} WebP-formatted compressed image
 *
 * @throws if file is not an image, or if compression fails
 */
export async function compressImage(file) {
  if (!file || !file.type.startsWith('image/')) {
    throw new Error('Please select an image file.');
  }

  // Step 1: Load the image into an <img> element
  const dataUrl = await readFileAsDataUrl(file);
  const img = await loadImage(dataUrl);

  // Step 2: Calculate target dimensions while preserving aspect ratio
  const { width, height } = scaleToMaxDimension(img.width, img.height, MAX_DIMENSION);

  // Step 3: Draw to canvas at target size
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  // High-quality smooth downscaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  // Step 4: Export canvas as WebP blob
  const blob = await canvasToBlob(canvas, OUTPUT_TYPE, QUALITY);
  if (!blob) throw new Error('Could not compress image — try a different photo.');

  return blob;
}

/**
 * Read a File as a data URL (for use in <img>.src)
 */
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read file.'));
    reader.readAsDataURL(file);
  });
}

/**
 * Load a data URL into an <img> element so canvas can draw it.
 */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load image — file may be corrupted.'));
    img.src = src;
  });
}

/**
 * Scale dimensions so the longer side equals max, preserving aspect ratio.
 * Skip scaling if image is already smaller than max.
 */
function scaleToMaxDimension(srcWidth, srcHeight, max) {
  if (srcWidth <= max && srcHeight <= max) {
    return { width: srcWidth, height: srcHeight };
  }
  const ratio = srcWidth > srcHeight
    ? max / srcWidth
    : max / srcHeight;
  return {
    width: Math.round(srcWidth * ratio),
    height: Math.round(srcHeight * ratio),
  };
}

/**
 * Promisify canvas.toBlob.
 */
function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, type, quality);
  });
}

/**
 * Determine the photo's "shape" for layout decisions in the UI.
 * Used by Wall feed to crop tall photos to 4:5, wide to 16:9, square stays square.
 *
 * @param {number} width
 * @param {number} height
 * @returns {'square'|'portrait'|'landscape'} shape category
 */
export function getPhotoShape(width, height) {
  const ratio = width / height;
  if (ratio > 1.2) return 'landscape';   // wider than 1.2:1 = landscape
  if (ratio < 0.85) return 'portrait';   // taller than 1:1.2 = portrait
  return 'square';                        // close to 1:1
}
/**
 * Detect the best display aspect ratio for an image.
 * Returns one of: 'square' (1:1), 'portrait' (4:5), 'landscape' (16:9).
 * Used for Instagram-style consistent feed display.
 */
export async function detectAspectRatio(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const ratio = img.width / img.height;
      URL.revokeObjectURL(url);
      // Tall portrait → 4:5 (Instagram's tallest)
      if (ratio < 0.85) resolve('portrait');
      // Wide landscape → 16:9
      else if (ratio > 1.3) resolve('landscape');
      // Squarish → 1:1
      else resolve('square');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve('square'); // fallback
    };
    img.src = url;
  });
}